<template>
  <div>
    <!-- Page Header -->
    <b-card>
      <b-row>
        <b-col
          cols="12"
          md="8"
        >
          <h3 class="mb-1">
            استيراد KML متعدد المشاريع
          </h3>
          <b-card-text class="mb-0">
            قم بتحميل ملف KML يحتوي على عدة مشاريع. سيتم تجميع المضلعات حسب الكود المساحي (Survey_Num) وإنشاء أو تحديث مواقع المشاريع تلقائياً.
          </b-card-text>
        </b-col>
      </b-row>
    </b-card>

    <!-- Import Form -->
    <b-card class="mt-2">
      <b-card-title>رفع ملف KML</b-card-title>

      <b-form @submit.prevent="handleImport">
        <b-row>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              label="اختر الملف"
              label-for="file-input"
            >
              <b-form-file
                id="file-input"
                v-model="importFile"
                :state="fileState"
                placeholder="اختر ملف KML (.kml )"
                drop-placeholder="اسحب الملف هنا"
                accept=".kml"
                :disabled="loading"
                @change="handleFileChange"
              />
              <b-form-invalid-feedback v-if="fileError">
                {{ fileError }}
              </b-form-invalid-feedback>
              <b-form-text v-if="importFile && !fileError">
                الملف المحدد: {{ importFile.name }} ({{ formatFileSize(importFile.size) }})
              </b-form-text>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="mt-2">
          <b-col cols="12">
            <b-button
              type="submit"
              variant="primary"
              :disabled="!importFile || loading || !!fileError"
              class="mr-1"
            >
              <feather-icon
                v-if="!loading"
                icon="UploadIcon"
                class="mr-50"
              />
              <b-spinner
                v-else
                small
                class="mr-50"
              />
              <span v-if="!loading">استيراد KML</span>
              <span v-else>جاري الاستيراد...</span>
            </b-button>

            <b-button
              variant="outline-secondary"
              :disabled="loading"
              @click="resetForm"
            >
              إعادة تعيين
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <!-- Results -->
    <b-card
      v-if="importResult"
      class="mt-2"
    >
      <b-card-title>
        <feather-icon
          :icon="importResult.success ? 'CheckCircleIcon' : 'AlertCircleIcon'"
          :class="importResult.success ? 'text-success' : 'text-danger'"
          class="mr-50"
        />
        نتائج الاستيراد
      </b-card-title>

      <div v-if="importResult.success && importResult.data">
        <!-- Statistics Summary -->
        <h5 class="mt-1 mb-1">
          إحصائيات الاستيراد
        </h5>
        <b-row class="mb-1">
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-primary">
              <h4 class="mb-0">
                {{ statistics.total_projects_in_kml || 0 }}
              </h4>
              <small>إجمالي المشاريع في KML</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-success">
              <h4 class="mb-0">
                {{ statistics.projects_processed || 0 }}
              </h4>
              <small>تم المعالجة</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-warning">
              <h4 class="mb-0">
                {{ statistics.projects_skipped || 0 }}
              </h4>
              <small>تم التخطي</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-danger">
              <h4 class="mb-0">
                {{ statistics.projects_with_errors || 0 }}
              </h4>
              <small>أخطاء</small>
            </b-card>
          </b-col>
        </b-row>

        <b-row class="mb-2">
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-info">
              <h4 class="mb-0">
                {{ statistics.project_locations_created || 0 }}
              </h4>
              <small>مواقع تم إنشاؤها</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-secondary">
              <h4 class="mb-0">
                {{ statistics.project_locations_updated || 0 }}
              </h4>
              <small>مواقع تم تحديثها</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-primary">
              <h4 class="mb-0">
                {{ statistics.total_polygons_processed || 0 }}
              </h4>
              <small>إجمالي المضلعات</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-success">
              <h4 class="mb-0">
                {{ statistics.total_coordinates_created || 0 }}
              </h4>
              <small>إجمالي الإحداثيات</small>
            </b-card>
          </b-col>
        </b-row>

        <b-row class="mb-2">
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-info">
              <h4 class="mb-0">
                {{ statistics.building_polygons_count || 0 }}
              </h4>
              <small>مضلعات المباني</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-secondary">
              <h4 class="mb-0">
                {{ statistics.land_polygons_count || 0 }}
              </h4>
              <small>مضلعات الأراضي</small>
            </b-card>
          </b-col>
        </b-row>

        <!-- Missing Survey Codes Alert -->
        <b-alert
          v-if="missingSurveyCodes && missingSurveyCodes.length > 0"
          variant="warning"
          show
          class="mt-2"
        >
          <h5 class="alert-heading">
            <feather-icon
              icon="AlertTriangleIcon"
              class="mr-50"
            />
            مشاريع غير موجودة
          </h5>
          <p class="mb-1">
            تم تخطي {{ missingSurveyCodes.length }} مشروع لأنها غير موجودة في قاعدة البيانات:
          </p>
          <ul class="mb-0">
            <li
              v-for="code in missingSurveyCodes"
              :key="code"
            >
              <strong>{{ code }}</strong>
            </li>
          </ul>
        </b-alert>

        <!-- Errors List -->
        <!-- <div v-if="errors && errors.length > 0" class="mt-2">
          <h5 class="mb-1 text-danger">قائمة الأخطاء</h5>
          <b-table
            :items="errors"
            :fields="errorFields"
            responsive
            striped
            hover
            small
          >
            <template #cell(survey_num)="data">
              <b-badge variant="info">
                {{ data.value }}
              </b-badge>
            </template>
            <template #cell(error)="data">
              <b-badge variant="danger">
                {{ data.value }}
              </b-badge>
            </template>
            <template #cell(message)="data">
              <span class="text-danger">{{ data.value }}</span>
            </template>
          </b-table>
        </div> -->
      </div>

      <div
        v-else-if="!importResult.success"
        class="text-danger"
      >
        <p><strong>فشل الاستيراد:</strong></p>
        <p>{{ importResult.error || importResult.message || 'حدث خطأ أثناء الاستيراد' }}</p>
        <div
          v-if="importResult.data && importResult.data.missing_survey_codes && importResult.data.missing_survey_codes.length > 0"
          class="mt-2"
        >
          <p class="mb-1">
            <strong>مشاريع غير موجودة:</strong>
          </p>
          <ul>
            <li
              v-for="code in importResult.data.missing_survey_codes"
              :key="code"
            >
              {{ code }}
            </li>
          </ul>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardText,
  BCardTitle,
  BRow,
  BCol,
  BButton,
  BForm,
  BFormGroup,
  BFormFile,
  BFormText,
  BFormInvalidFeedback,
  BTable,
  BBadge,
  BSpinner,
  BAlert,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Projects from '@/api/projects/projects'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'

export default {
  name: 'MultiProjectsKMLImport',
  components: {
    BCard,
    BCardText,
    BCardTitle,
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    BFormFile,
    BFormText,
    BFormInvalidFeedback,
    BTable,
    BBadge,
    BSpinner,
    BAlert,
    FeatherIcon,
  },
  data() {
    return {
      importFile: null,
      loading: false,
      fileError: null,
      importResult: null,
      errorFields: [
        { key: 'survey_num', label: 'الكود المساحي', sortable: true },
        { key: 'error', label: 'نوع الخطأ', sortable: true },
        { key: 'message', label: 'الرسالة', sortable: false },
      ],
    }
  },
  computed: {
    fileState() {
      if (this.fileError) return false
      if (this.importFile) return true
      return null
    },
    statistics() {
      return this.importResult?.data?.statistics || {}
    },
    missingSurveyCodes() {
      return this.importResult?.data?.missing_survey_codes || []
    },
    errors() {
      return this.importResult?.data?.errors || []
    },
  },
  methods: {
    handleFileChange(event) {
      this.fileError = null
      this.importResult = null

      const file = event?.target?.files?.[0]
      if (!file) {
        this.importFile = null
        return
      }

      const validExtensions = ['.kml']
      const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`
      if (!validExtensions.includes(fileExtension)) {
        this.fileError = 'يجب أن يكون الملف من نوع KML (.kml)'
        this.importFile = null
        return
      }

      const maxSize = 50 * 1024 * 1024 // 50 MB
      if (file.size > maxSize) {
        this.fileError = 'حجم الملف يجب أن يكون أقل من 50 ميجابايت'
        this.importFile = null
        return
      }

      this.importFile = file
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return `${Math.round(bytes / Math.pow(k, i) * 100) / 100} ${sizes[i]}`
    },

    async handleImport() {
      if (!this.importFile || this.fileError) {
        this.showError('يرجى اختيار ملف صحيح')
        return
      }

      this.loading = true
      this.importResult = null

      try {
        const formData = new FormData()
        formData.append('file', this.importFile)

        const projectsAPI = new Projects()
        const response = await projectsAPI.importMultiProjectsKML(formData)

        // Handle different response formats
        if (response.status === 'success' || response.success !== false) {
          this.importResult = {
            success: true,
            data: response.data || response,
            message: response.message,
          }

          const stats = this.statistics
          if (stats.projects_processed > 0) {
            this.showSuccess(
              `تم استيراد ${stats.projects_processed} مشروع بنجاح. `
              + `تم إنشاء ${stats.project_locations_created} موقع وتحديث ${stats.project_locations_updated} موقع.`,
            )
          } else {
            this.showWarning('لم يتم استيراد أي مشاريع. تحقق من الأكواد المساحية المفقودة.')
          }
        } else {
          this.importResult = {
            success: false,
            error: response.message || 'فشل الاستيراد',
            data: response.data,
          }
          this.showError(response.message || 'فشل استيراد ملف KML')
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.message
                           || error?.message
                           || 'حدث خطأ أثناء استيراد ملف KML'

        this.importResult = {
          success: false,
          error: errorMessage,
          data: error?.response?.data?.data || null,
        }

        // Handle specific error messages
        if (error?.response?.data?.message === 'no_polygons_found_in_kml') {
          this.showError('لم يتم العثور على مضلعات في ملف KML')
        } else if (error?.response?.data?.message === 'no_survey_numbers_found_in_kml') {
          this.showError('لم يتم العثور على أكواد مساحية في ملف KML')
        } else if (error?.response?.data?.message === 'failed_to_import_any_projects') {
          this.showError('فشل استيراد أي مشاريع. تحقق من الأكواد المساحية المفقودة.')
        } else {
          this.showError(errorMessage)
        }
      } finally {
        this.loading = false
      }
    },

    resetForm() {
      this.importFile = null
      this.fileError = null
      this.importResult = null
      // Reset file input
      const fileInput = document.getElementById('file-input')
      if (fileInput) {
        fileInput.value = ''
      }
    },

    showSuccess(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'نجح',
          icon: 'CheckIcon',
          variant: 'success',
          text: message,
        },
      })
    },

    showError(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'خطأ',
          icon: 'AlertCircleIcon',
          variant: 'danger',
          text: message,
        },
      })
    },

    showWarning(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'تحذير',
          icon: 'AlertTriangleIcon',
          variant: 'warning',
          text: message,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.bg-light-primary {
  background-color: rgba(115, 103, 240, 0.1) !important;
}

.bg-light-success {
  background-color: rgba(40, 199, 111, 0.1) !important;
}

.bg-light-warning {
  background-color: rgba(255, 159, 67, 0.1) !important;
}

.bg-light-danger {
  background-color: rgba(234, 84, 85, 0.1) !important;
}

.bg-light-info {
  background-color: rgba(0, 207, 232, 0.1) !important;
}

.bg-light-secondary {
  background-color: rgba(130, 134, 139, 0.1) !important;
}
</style>
